import { useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Resizable } from 're-resizable';

const useStyles = makeStyles(
  () => ({
    draggable: props => ({
      display: 'inline-block',
      position: 'absolute',
      cursor: 'move',
      zIndex: props.isSelect ? 1000 : 3,
    }),
    resizable: props => ({
      display: 'inline-block',
      border: 'solid 1px #2ca6e0',
      backgroundColor: props.isSelect
        ? 'rgba(44, 166, 224, 0.7)'
        : 'rgba(44, 166, 224, 0.4)',
      '&:hover': {
        border: 'solid 2px #2ca6e0',
      },
    }),
    square: {
      width: 6,
      height: 6,
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.13)',
      border: 'solid 1px #2ca6e0',
      backgroundColor: '#FFFFFF',
    },
  }),
  { name: 'Rnd' },
);

export default function Rnd({
  className,
  position,
  size,
  isSelect,
  children,
  maxWidth,
  maxHeight,
  minWidth,
  minHeight,
  boundsId,
  onResizeStart,
  onResizeStop,
  onDragStart,
  onDragStop,
  onContextMenu,
}) {
  const classes = useStyles({ isSelect });
  const boxRef = useRef(null);
  const { x, y } = position;
  const { width, height } = size;

  let isResizeStart = false;
  let isDragStart = false;

  const onResizeStartHandle = () => {
    isResizeStart = true;
    onResizeStart();
  };

  const onResizeStopHandle = (e, direction, ref) => {
    onResizeStop(e, direction, ref);
    isResizeStart = false;
  };

  const onDragHandle = e => {
    e.preventDefault();
    e.stopPropagation();
    onDragStart(e);

    let { clientX: startX, clientY: startY } = e;
    const startXTmp = startX;
    const startYTmp = startY;

    let XTmp = x;
    let YTmp = y;
    if (isResizeStart) return;
    isDragStart = true;
    const onMove = eM => {
      if (!isDragStart) return;
      eM.stopImmediatePropagation();
      const { clientX, clientY } = eM;
      XTmp += clientX - startX;
      YTmp += clientY - startY;
      if (XTmp <= 0) XTmp = 0;
      if (XTmp >= maxWidth - width) XTmp = maxWidth - width;
      if (YTmp <= 0) YTmp = 0;
      if (YTmp >= maxHeight - height) YTmp = maxHeight - height;
      boxRef.current.style.top = `${YTmp}px`;
      boxRef.current.style.left = `${XTmp}px`;
      startX = clientX;
      startY = clientY;
    };
    const onUp = eU => {
      const { clientX, clientY } = eU;
      let deltaX = x + clientX - startXTmp;
      let deltaY = y + clientY - startYTmp;
      if (deltaX <= 0) deltaX = 0;
      if (deltaX >= maxWidth - width) deltaX = maxWidth - width;
      if (deltaY <= 0) deltaY = 0;
      if (deltaY >= maxHeight - height) deltaY = maxHeight - height;
      onDragStop(eU, deltaX, deltaY);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('mouseleave', onUp);
      if (!isDragStart) return;
      isDragStart = false;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('mouseleave', onUp);
  };

  const onContextMenuHandle = e => {
    e.preventDefault();
    onContextMenu(e);
  };

  return (
    <div
      ref={boxRef}
      className={`${classes.draggable} ${className.draggable || ''}`}
      style={{ top: y, left: x }}
      onMouseDown={onDragHandle}
      onContextMenu={onContextMenuHandle}
    >
      <Resizable
        className={`${classes.resizable} ${className.resizable || ''}`}
        size={{ width, height }}
        onResizeStart={onResizeStartHandle}
        onResizeStop={onResizeStopHandle}
        minWidth={minWidth}
        minHeight={minHeight}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        bounds={document.getElementById(boundsId)}
        enable={{
          top: false,
          right: false,
          bottom: false,
          left: false,
          topRight: isSelect,
          bottomRight: isSelect,
          bottomLeft: isSelect,
          topLeft: isSelect,
        }}
        handleStyles={{
          topRight: {
            width: 0,
            height: 0,
            cursor: 'nesw-resize',
            right: 3,
            top: -3,
          },
          bottomRight: {
            width: 0,
            height: 0,
            cursor: 'nwse-resize',
            right: 3,
            bottom: 3,
          },
          bottomLeft: {
            width: 0,
            height: 0,
            cursor: 'nesw-resize',
            left: -3,
            bottom: 3,
          },
          topLeft: {
            width: 0,
            height: 0,
            cursor: 'nwse-resize',
            left: -3,
            top: -3,
          },
        }}
        handleComponent={{
          topRight: <div className={classes.square} />,
          bottomRight: <div className={classes.square} />,
          bottomLeft: <div className={classes.square} />,
          topLeft: <div className={classes.square} />,
        }}
      >
        {children && children}
      </Resizable>
    </div>
  );
}

Rnd.defaultProps = {
  className: {},
  isSelect: false,
  children: undefined,
  maxWidth: '100%',
  maxHeight: '100%',
  minWidth: 30,
  minHeight: 30,
  onResizeStart: () => {},
  onResizeStop: () => {},
  onDragStart: () => {},
  onDragStop: () => {},
  onContextMenu: () => {},
};

Rnd.propTypes = {
  className: PropTypes.shape({
    draggable: PropTypes.node,
    resizable: PropTypes.node,
  }),
  position: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }).isRequired,
  size: PropTypes.shape({
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  isSelect: PropTypes.bool,
  children: PropTypes.node,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  boundsId: PropTypes.string.isRequired,
  onResizeStart: PropTypes.func,
  onResizeStop: PropTypes.func,
  onDragStart: PropTypes.func,
  onDragStop: PropTypes.func,
  onContextMenu: PropTypes.func,
};
